<template>
    <div class="card">
        <header class="card-header">
            <p class="card-header-title">About {{ project.name }}</p>
        </header>
        <div class="card-content">
            <table class="table is-fullwidth about-project-table">
                <tbody>
                    <tr>
                        <th>Created</th>
                        <td>{{ dateString }}</td>
                    </tr>
                    <tr>
                        <th>License</th>
                        <td>
                            <a
                                href="https://creativecommons.org/licenses/by/4.0/"
                                target="_blank"
                                rel="noopener noreferrer">
                                Creative Commons Attribution 4.0 International
                            </a>
                        </td>
                    </tr>
                    <tr v-if="doiUrl">
                        <th>DOI</th>
                        <td>
                            <a :href="doiUrl" target="_blank" rel="noopener noreferrer">
                                <code>{{ doiUrl }}</code>
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Immutable BossDB URI</th>
                        <td>
                            <a :href="projectUrl">
                                <code>{{ projectUrl }}</code>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <hr />

            <div class="citation-actions">
                <p class="label citation-label">Copy citation to clipboard</p>
                <div class="buttons">
                    <button
                        v-for="format in citationFormats"
                        :key="format.value"
                        :class="['button', format.className]"
                        @click="copyCitation(format.value)">
                        {{ format.label }}
                    </button>
                </div>
                <p class="help">
                    Each format includes the dataset citation and the BossDB paper
                    (Hider et al., 2022).
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { Notyf } from "notyf";
import "notyf/notyf.min.css";
import { buildCombinedCitation, doiToUrl } from "@/bossdb_citation.js";

const notyf = new Notyf();

export default {
    name: "ProjectInfoCard",
    props: {
        project: {
            type: Object,
            required: true,
        },
    },
    computed: {
        citationFormats() {
            return [
                { label: "BibTeX", value: "bibtex", className: "is-dark" },
                { label: "APA", value: "apa", className: "is-link" },
                { label: "MLA", value: "mla", className: "is-info" },
            ];
        },
        dateString() {
            return new Date(this.project.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        doiUrl() {
            return doiToUrl(this.project?.doi);
        },
        projectUrl() {
            return `https://bossdb.org/project/${this.project.id}`;
        },
    },
    methods: {
        async copyCitation(format) {
            try {
                const citation = await buildCombinedCitation(this.project, format);
                await navigator.clipboard.writeText(citation);
                notyf.success(`Copied ${format.toUpperCase()} citation to clipboard.`);
            } catch (err) {
                notyf.error("Unable to copy citation to clipboard.");
            }
        },
    },
};
</script>

<style scoped>
.about-project-table {
    margin-bottom: 0;
}

.about-project-table th {
    width: 10.5rem;
    white-space: nowrap;
    vertical-align: top;
}

.about-project-table td {
    word-break: break-word;
}

.about-project-table code {
    white-space: normal;
}

.citation-actions {
    margin-top: 1rem;
}

.citation-label {
    margin-bottom: 0.75rem;
}

.citation-actions .buttons {
    margin-bottom: 0.5rem;
}
</style>
